import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/Context';
import { usePermission } from '../hooks/usePermission';
import Popup from './Model/Popup';
import * as Authservices from '../Services/services';
import { SUPER_ADMIN_ROLE, getRoleName } from '../config/permissions';

const ROLE_COLORS = ['#f82581', '#6366f1', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];

/**
 * The platform must never be left without administrators, so a Super Admin
 * account can't be deleted or demoted while it would take the count below this.
 */
const MIN_SUPER_ADMINS = 2;

/** Must satisfy the same rule the Change Password screen enforces. */
const PASSWORD_RULE = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*_])[A-Za-z\d!@#$%^&*_]{8,}$/;
const PASSWORD_HINT = 'At least 8 characters, with one uppercase letter, one digit and one special character.';

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';   // no I/O — easy to misread when shared
const LOWER = 'abcdefghijkmnopqrstuvwxyz'; // no l
const DIGITS = '23456789';                 // no 0/1
const SPECIALS = '!@#$%^&*_';

const randomInt = (max) => {
    const crypto = window.crypto || window.msCrypto;
    if (crypto?.getRandomValues) {
        const buf = new Uint32Array(1);
        crypto.getRandomValues(buf);
        return buf[0] % max;
    }
    return Math.floor(Math.random() * max);
};

const pick = (chars) => chars[randomInt(chars.length)];

/** Generates a 14-char temporary password guaranteed to pass PASSWORD_RULE. */
const generateTempPassword = () => {
    const pool = UPPER + LOWER + DIGITS + SPECIALS;
    const chars = [pick(UPPER), pick(LOWER), pick(DIGITS), pick(SPECIALS)];
    while (chars.length < 14) chars.push(pick(pool));
    // Fisher–Yates, so the guaranteed characters aren't always in the same slots.
    for (let i = chars.length - 1; i > 0; i -= 1) {
        const j = randomInt(i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
};

const getInitials = (name) => (name || '').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
const getColor = (key) => ROLE_COLORS[(key || '?').charCodeAt(0) % ROLE_COLORS.length] || '#f82581';

const adminRoleName = (admin) => getRoleName(admin?.role) || admin?.role_name || null;
const adminRoleId = (admin) => admin?.role_id ?? (typeof admin?.role === 'object' ? admin?.role?.id : null) ?? null;
const roleLabel = (role) => role?.label || role?.display_name || role?.name || '—';
const isSuperAdminAccount = (admin) => adminRoleName(admin) === SUPER_ADMIN_ROLE;

const AdminUsers = () => {
    const { user: currentUser } = useAuth();
    const { canView, canEdit, isSuperAdmin } = usePermission();

    const [admins, setAdmins] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createData, setCreateData] = useState({ name: '', email: '', password: '', role_id: '' });
    const [createErrors, setCreateErrors] = useState({});
    const [creating, setCreating] = useState(false);
    const [createdResult, setCreatedResult] = useState(null);

    const [editData, setEditData] = useState(null);
    const [saving, setSaving] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [resettingId, setResettingId] = useState(null);
    const [resetResult, setResetResult] = useState(null);
    const [confirmReset, setConfirmReset] = useState(null);
    const [resetError, setResetError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setFetchError(null);

        const [adminsResult, rolesResult] = await Promise.allSettled([
            Authservices.getAdminUsers(),
            Authservices.getRoles(),
        ]);

        if (adminsResult.status === 'fulfilled') {
            const res = adminsResult.value;
            const list = res.data?.responseDetails?.admins
                || res.data?.responseDetails
                || res.data?.data?.admins
                || res.data?.data
                || [];
            setAdmins(Array.isArray(list) ? list : list?.rows || []);
        } else {
            console.error('[AdminUsers] fetch error:', adminsResult.reason);
            const status = adminsResult.reason?.response?.status;
            if (status === 500) {
                setFetchError('The team members API is temporarily unavailable (server error). Please try again later.');
            } else if (status === 404) {
                setFetchError('Team members endpoint not found. Ensure the backend exposes GET /admin/v1/rbac/admin-users.');
            } else {
                setFetchError('Failed to load team members. Please refresh and try again.');
            }
        }

        if (rolesResult.status === 'fulfilled') {
            const res = rolesResult.value;
            const list = res.data?.responseDetails || res.data?.data || [];
            setRoles(Array.isArray(list) ? list : list?.rows || []);
        }

        setLoading(false);
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const superAdminCount = useMemo(
        () => admins.filter(isSuperAdminAccount).length,
        [admins],
    );

    /**
     * Returns a reason string when a Super Admin account is protected from
     * removal/demotion, or null when the action is allowed.
     */
    const superAdminLockReason = useCallback((admin) => {
        if (!isSuperAdminAccount(admin)) return null;
        if (superAdminCount > MIN_SUPER_ADMINS) return null;
        return superAdminCount <= 1
            ? 'This is the only Super Admin account — it cannot be removed or reassigned.'
            : `The platform must keep at least ${MIN_SUPER_ADMINS} Super Admin accounts. Create another Super Admin before removing or reassigning this one.`;
    }, [superAdminCount]);

    const filtered = admins.filter((a) => {
        const q = search.trim().toLowerCase();
        const matchSearch = !q
            || a.name?.toLowerCase().includes(q)
            || a.email?.toLowerCase().includes(q);
        const matchRole = roleFilter === 'all' || adminRoleName(a) === roleFilter;
        const matchStatus = statusFilter === 'all' || a.status === statusFilter;
        return matchSearch && matchRole && matchStatus;
    });

    const roleCounts = {};
    admins.forEach((a) => {
        const key = adminRoleName(a) || 'unassigned';
        roleCounts[key] = (roleCounts[key] || 0) + 1;
    });

    /* ── Create ──────────────────────────────────────────────────────────── */

    const validateCreate = () => {
        const errs = {};
        if (!createData.name.trim()) errs.name = 'Full name is required';
        if (!createData.email.trim()) errs.email = 'Email address is required';
        else if (!/^\S+@\S+\.\S+$/.test(createData.email.trim())) errs.email = 'Enter a valid email address';
        if (!createData.password.trim()) errs.password = 'A temporary password is required';
        // No "create without a role" path exists — the backend rejects it too.
        if (!createData.role_id) errs.role_id = 'A role must be selected before the account can be created';
        setCreateErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const createReady = createData.name.trim() && createData.email.trim()
        && createData.password.trim() && !!createData.role_id;

    const openCreateModal = () => {
        setCreateErrors({});
        setCreateData({ name: '', email: '', password: generateTempPassword(), role_id: '' });
        setShowCreateModal(true);
    };

    const handleCreate = async () => {
        if (!validateCreate()) return;
        const password = createData.password.trim();
        if (!PASSWORD_RULE.test(password)) {
            setCreateErrors((p) => ({ ...p, password: PASSWORD_HINT }));
            return;
        }

        setCreating(true);
        try {
            const res = await Authservices.createAdminUser({
                name: createData.name.trim(),
                email: createData.email.trim(),
                password,
                role_id: Number(createData.role_id) || createData.role_id,
            });
            if (res?.status >= 200 && res.status < 300) {
                setShowCreateModal(false);
                setCreateErrors({});
                // We chose the password, so we can always hand it back for copying.
                setCreatedResult({ name: createData.name.trim(), email: createData.email.trim(), password });
                setCreateData({ name: '', email: '', password: '', role_id: '' });
                fetchData();
            }
        } catch (err) {
            const body = err?.response?.data;
            if (body?.errorType === 'ValidationError' && /role_id/i.test(body?.message || '')) {
                setCreateErrors((p) => ({ ...p, role_id: body.responseMessage || body.message }));
            }
            toast.error(body?.responseMessage || 'Failed to create team member');
        } finally {
            setCreating(false);
        }
    };

    /* ── Edit ────────────────────────────────────────────────────────────── */

    const openEdit = (admin) => {
        setEditData({
            id: admin.id,
            name: admin.name || '',
            status: admin.status || 'active',
            role_id: adminRoleId(admin) ?? '',
            originalRoleId: adminRoleId(admin) ?? '',
            isSuperAdminAccount: isSuperAdminAccount(admin),
            lockReason: superAdminLockReason(admin),
        });
    };

    const handleSaveEdit = async () => {
        if (!editData) return;
        const roleChanged = String(editData.role_id) !== String(editData.originalRoleId);

        if (roleChanged && editData.lockReason) {
            toast.error(editData.lockReason);
            return;
        }

        setSaving(true);
        try {
            const payload = { name: editData.name, status: editData.status };
            // Sending role_id makes this a Super-Admin-only call, so only include
            // it when a Super Admin actually changed the assignment.
            if (isSuperAdmin && roleChanged && editData.role_id) {
                payload.role_id = Number(editData.role_id) || editData.role_id;
            }

            const res = await Authservices.updateAdminUser(editData.id, payload);
            if (res?.status >= 200 && res.status < 300) {
                toast.success('Team member updated');
                if (payload.role_id) {
                    toast.info('Role reassigned — that admin has been signed out and must log in again.');
                }
                setEditData(null);
                fetchData();
            }
        } catch (err) {
            toast.error(err?.response?.data?.responseMessage || 'Failed to update team member');
        } finally {
            setSaving(false);
        }
    };

    /* ── Delete ──────────────────────────────────────────────────────────── */

    const requestDelete = (admin) => {
        const lock = superAdminLockReason(admin);
        if (lock) {
            toast.error(lock);
            return;
        }
        setConfirmDelete(admin);
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            const res = await Authservices.deleteAdminUser(confirmDelete.id);
            if (res?.status >= 200 && res.status < 300) {
                toast.success('Team member removed');
                setConfirmDelete(null);
                fetchData();
            }
        } catch (err) {
            toast.error(err?.response?.data?.responseMessage || 'Failed to remove team member');
        } finally {
            setDeleting(false);
        }
    };

    /* ── Reset password (Super Admin only) ───────────────────────────────── */

    const openResetModal = (admin) => {
        setResetError(null);
        setConfirmReset({ admin, password: generateTempPassword() });
    };

    const handleResetPassword = async () => {
        if (!confirmReset) return;
        const password = confirmReset.password.trim();

        if (!PASSWORD_RULE.test(password)) {
            setResetError(PASSWORD_HINT);
            return;
        }

        const admin = confirmReset.admin;
        setResettingId(admin.id);
        try {
            const res = await Authservices.resetAdminUserPassword(admin.id, password);
            if (res?.status >= 200 && res.status < 300) {
                setConfirmReset(null);
                setResetError(null);
                // We chose the password, so we can always show it back.
                setResetResult({ admin, tempPassword: password, message: res.data?.responseMessage });
            }
        } catch (err) {
            const body = err?.response?.data;
            setResetError(body?.responseMessage || body?.message || 'Failed to reset password');
        } finally {
            setResettingId(null);
        }
    };

    const handleStatusToggle = async (admin) => {
        const newStatus = admin.status === 'active' ? 'inactive' : 'active';
        try {
            const res = await Authservices.updateAdminUser(admin.id, { status: newStatus });
            if (res?.status >= 200 && res.status < 300) {
                toast.success(`Team member ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
                setAdmins((prev) => prev.map((a) => (a.id === admin.id ? { ...a, status: newStatus } : a)));
            }
        } catch (err) {
            toast.error(err?.response?.data?.responseMessage || 'Failed to update status');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard?.writeText(text)
            .then(() => toast.success('Temporary password copied'))
            .catch(() => toast.error('Could not copy — please select and copy it manually'));
    };

    if (!canView('admin_users')) return null;

    const uniqueRoles = [...new Set(admins.map(adminRoleName).filter(Boolean))];

    return (
        <section className="admin-users-section section">
            <div className="rbac-header">
                <div>
                    <h4>Team Members</h4>
                    <p className="rbac-subtitle">
                        {loading
                            ? 'Loading…'
                            : `${admins.length} team member${admins.length === 1 ? '' : 's'} across ${Object.keys(roleCounts).length} role${Object.keys(roleCounts).length === 1 ? '' : 's'}`}
                    </p>
                </div>
                {/* Creating an account assigns a role, so it is Super Admin only. */}
                {isSuperAdmin && (
                    <button className="btn btn-primary" onClick={openCreateModal} disabled={loading}>
                        <i className="fas fa-user-plus"></i> Add Member
                    </button>
                )}
            </div>

            {fetchError && (
                <div style={{ margin: '12px 0', padding: '12px 16px', background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.32)', borderRadius: 8, color: '#f87171', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <i className="fas fa-exclamation-circle" style={{ marginTop: 2, flexShrink: 0 }}></i>
                    <span style={{ fontSize: 14 }}>{fetchError}</span>
                </div>
            )}

            {!loading && superAdminCount > 0 && superAdminCount <= MIN_SUPER_ADMINS && isSuperAdmin && (
                <div style={{ margin: '12px 0', padding: '10px 14px', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', borderRadius: 8, color: '#f59e0b', display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13 }}>
                    <i className="fas fa-shield-alt" style={{ marginTop: 2 }}></i>
                    <span>
                        {superAdminCount === 1
                            ? 'Only one Super Admin account exists. It is locked against removal and reassignment — add a second Super Admin to restore normal management.'
                            : `The platform keeps at least ${MIN_SUPER_ADMINS} Super Admin accounts. These accounts are locked against removal and reassignment until another Super Admin is added.`}
                    </span>
                </div>
            )}

            {!loading && (
                <div className="admin-users-stats">
                    {Object.entries(roleCounts).map(([role, count]) => (
                        <div key={role} className="admin-stat-chip" style={{ borderColor: `${getColor(role)}30` }}>
                            <span className="admin-stat-dot" style={{ background: getColor(role) }}></span>
                            <span>{role}</span>
                            <strong>{count}</strong>
                        </div>
                    ))}
                </div>
            )}

            <div className="admin-users-filters">
                <div className="admin-search-box">
                    <i className="fas fa-search"></i>
                    <input type="text" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <select className="admin-filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                    <option value="all">All Roles</option>
                    {uniqueRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <div className="admin-status-tabs">
                    {['all', 'active', 'inactive'].map((s) => (
                        <button key={s} className={`admin-status-tab ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
                            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                            <span className="admin-status-count">
                                {s === 'all' ? admins.length : admins.filter((a) => a.status === s).length}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="admin-users-table-wrap">
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 60, color: '#8a8a9a' }}>
                        <i className="fas fa-spinner fa-spin" style={{ fontSize: 32, marginBottom: 12 }}></i>
                        <p>Loading team members…</p>
                    </div>
                ) : (
                    <table className="admin-users-table">
                        <thead>
                            <tr>
                                <th>Member</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Last Login</th>
                                <th>Joined</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((admin) => {
                                const roleName = adminRoleName(admin);
                                const color = getColor(roleName);
                                const isSelf = admin.id === currentUser?.id;
                                const lock = superAdminLockReason(admin);
                                return (
                                    <tr key={admin.id}>
                                        <td>
                                            <div className="admin-user-cell">
                                                <div className="admin-avatar" style={{ background: `${color}25`, color }}>
                                                    {admin.profile_image
                                                        ? <img src={admin.profile_image} alt={admin.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                        : getInitials(admin.name)}
                                                </div>
                                                <div>
                                                    <strong>
                                                        {admin.name}
                                                        {isSelf && <span style={{ marginLeft: 6, fontSize: 11, color: '#8a8a9a' }}>(you)</span>}
                                                    </strong>
                                                    <span className="admin-email">{admin.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="admin-role-badge" style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
                                                {roleName || 'unassigned'}
                                            </span>
                                            {lock && (
                                                <i className="fas fa-lock" title={lock} style={{ marginLeft: 8, fontSize: 11, color: '#f59e0b' }}></i>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`admin-status-badge ${admin.status}`}>
                                                <span className="admin-status-indicator"></span>
                                                {admin.status}
                                            </span>
                                            {admin.must_change_password && (
                                                <span title="Must change password at next login" style={{ marginLeft: 8, fontSize: 11, color: '#f59e0b' }}>
                                                    <i className="fas fa-key"></i>
                                                </span>
                                            )}
                                        </td>
                                        <td className="admin-meta">
                                            {admin.last_login ? new Date(admin.last_login).toLocaleString() : 'Never'}
                                        </td>
                                        <td className="admin-meta">
                                            {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '—'}
                                        </td>
                                        <td>
                                            <div className="admin-actions">
                                                {canEdit('admin_users') && (
                                                    <button className="admin-action-btn" title="Edit" onClick={() => openEdit(admin)}>
                                                        <i className="fas fa-pencil-alt"></i>
                                                    </button>
                                                )}
                                                {canEdit('admin_users') && !isSelf && (
                                                    <button
                                                        className="admin-action-btn"
                                                        title={admin.status === 'active' ? 'Deactivate' : 'Activate'}
                                                        onClick={() => handleStatusToggle(admin)}
                                                    >
                                                        <i className={`fas ${admin.status === 'active' ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                                                    </button>
                                                )}
                                                {/* Reset Password is Super Admin only — absent, not disabled, for everyone else. */}
                                                {isSuperAdmin && !isSelf && (
                                                    <button
                                                        className="admin-action-btn"
                                                        title="Reset Password"
                                                        onClick={() => openResetModal(admin)}
                                                        disabled={resettingId === admin.id}
                                                    >
                                                        <i className={`fas ${resettingId === admin.id ? 'fa-spinner fa-spin' : 'fa-key'}`}></i>
                                                    </button>
                                                )}
                                                {canEdit('admin_users') && !isSelf && !lock && (
                                                    <button className="admin-action-btn danger" title="Remove" onClick={() => requestDelete(admin)}>
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="admin-empty">
                        <i className="fas fa-users-slash"></i>
                        <p>No team members found matching your filters</p>
                    </div>
                )}
            </div>

            {/* ── Add member ────────────────────────────────────────────────── */}
            {showCreateModal && (
                <Popup
                    title="Add Team Member"
                    icon="fas fa-user-plus"
                    maxWidth={520}
                    onClose={() => setShowCreateModal(false)}
                    footer={<>
                        <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                        <button className="btn" onClick={handleCreate} disabled={creating || !createReady}>
                            {creating ? <><i className="fas fa-spinner fa-spin"></i> Creating…</> : 'Add Member'}
                        </button>
                    </>}
                >
                    {[
                        { label: 'Full Name', key: 'name', type: 'text', placeholder: 'Enter full name' },
                        { label: 'Email Address', key: 'email', type: 'email', placeholder: 'Enter email address' },
                    ].map((f) => (
                        <div className="form-group" key={f.key}>
                            <label htmlFor={`new-${f.key}`}>{f.label}</label>
                            <input
                                id={`new-${f.key}`} type={f.type} placeholder={f.placeholder}
                                value={createData[f.key]}
                                onChange={(e) => {
                                    setCreateData((prev) => ({ ...prev, [f.key]: e.target.value }));
                                    setCreateErrors((prev) => ({ ...prev, [f.key]: undefined }));
                                }}
                            />
                            {createErrors[f.key] && <span className="text-danger pt-2">{createErrors[f.key]}</span>}
                        </div>
                    ))}

                    <div className="form-group">
                        <label htmlFor="new-password">Temporary Password</label>
                        <div className="admin-generate-row">
                            <input
                                id="new-password"
                                type="text"
                                value={createData.password}
                                onChange={(e) => {
                                    setCreateData((prev) => ({ ...prev, password: e.target.value }));
                                    setCreateErrors((prev) => ({ ...prev, password: undefined }));
                                }}
                            />
                            <button
                                type="button" className="btn btn-secondary" title="Generate a new one"
                                onClick={() => {
                                    setCreateData((prev) => ({ ...prev, password: generateTempPassword() }));
                                    setCreateErrors((prev) => ({ ...prev, password: undefined }));
                                }}
                            >
                                <i className="fas fa-rotate"></i> Regenerate
                            </button>
                        </div>
                        {createErrors.password
                            ? <span className="text-danger pt-2">{createErrors.password}</span>
                            : <span className="admin-field-note">{PASSWORD_HINT}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="new-role">Role <span style={{ color: '#ef4444' }}>*</span></label>
                        <select
                            id="new-role"
                            value={createData.role_id}
                            onChange={(e) => {
                                setCreateData((prev) => ({ ...prev, role_id: e.target.value }));
                                setCreateErrors((prev) => ({ ...prev, role_id: undefined }));
                            }}
                        >
                            <option value="">Select a role</option>
                            {roles.map((r) => <option key={r.id} value={r.id}>{roleLabel(r)}</option>)}
                        </select>
                        {createErrors.role_id && <span className="text-danger pt-2">{createErrors.role_id}</span>}
                    </div>

                    <p className="admin-modal-hint">
                        <i className="fas fa-info-circle"></i>
                        After creating the account you'll get one chance to copy this password. Share it with them
                        directly — they must set their own password before they can reach any section.
                    </p>
                </Popup>
            )}

            {/* ── Created member / temporary password ─────────────────────────── */}
            {createdResult && (
                <Popup
                    title={`${createdResult.name} was added`}
                    icon="fas fa-check-circle"
                    iconColor="#10b981"
                    maxWidth={500}
                    onClose={() => setCreatedResult(null)}
                    footer={
                        <button className="btn" onClick={() => setCreatedResult(null)}>Done</button>
                    }
                >
                    <p className="admin-modal-text">
                        Copy this temporary password and share it with <strong>{createdResult.email}</strong> directly.
                        Once you close this dialog it cannot be shown again.
                    </p>
                    <div className="admin-temp-password">
                        <code>{createdResult.password}</code>
                        <button className="btn btn-secondary" onClick={() => copyToClipboard(createdResult.password)}>
                            <i className="fas fa-copy"></i> Copy
                        </button>
                    </div>

                    <p className="admin-modal-hint">
                        <i className="fas fa-info-circle"></i>
                        They'll be sent straight to the Change Password screen at first login and cannot reach any
                        section until they set their own password.
                    </p>
                </Popup>
            )}

            {/* ── Edit member ───────────────────────────────────────────────── */}
            {editData && (
                <Popup
                    title="Edit Team Member"
                    icon="fas fa-pencil-alt"
                    maxWidth={520}
                    onClose={() => setEditData(null)}
                    footer={<>
                        <button className="btn btn-secondary" onClick={() => setEditData(null)}>Cancel</button>
                        <button className="btn" onClick={handleSaveEdit} disabled={saving}>
                            {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving…</> : 'Save Changes'}
                        </button>
                    </>}
                >
                    <div className="form-group">
                        <label htmlFor="edit-name">Full Name</label>
                        <input
                            id="edit-name" type="text" value={editData.name}
                            onChange={(e) => setEditData((prev) => ({ ...prev, name: e.target.value }))}
                        />
                    </div>

                    {/* Role assignment exists only for Super Admins — hidden, not disabled. */}
                    {isSuperAdmin && (
                        <div className="form-group">
                            <label htmlFor="edit-role">Role</label>
                            <select
                                id="edit-role"
                                value={editData.role_id}
                                disabled={!!editData.lockReason}
                                onChange={(e) => setEditData((prev) => ({ ...prev, role_id: e.target.value }))}
                            >
                                <option value="">Select a role</option>
                                {roles.map((r) => <option key={r.id} value={r.id}>{roleLabel(r)}</option>)}
                            </select>
                            {editData.lockReason ? (
                                <span className="admin-field-note locked">
                                    <i className="fas fa-lock"></i>{editData.lockReason}
                                </span>
                            ) : (
                                <span className="admin-field-note">
                                    Changing the role signs this admin out immediately.
                                </span>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="edit-status">Status</label>
                        <select
                            id="edit-status" value={editData.status}
                            onChange={(e) => setEditData((prev) => ({ ...prev, status: e.target.value }))}
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </Popup>
            )}

            {/* ── Confirm reset password ────────────────────────────────────── */}
            {confirmReset && (
                <Popup
                    title={`Reset password for ${confirmReset.admin.name}`}
                    icon="fas fa-key"
                    iconColor="#f59e0b"
                    maxWidth={520}
                    onClose={() => { setConfirmReset(null); setResetError(null); }}
                    footer={<>
                        <button className="btn btn-secondary" onClick={() => { setConfirmReset(null); setResetError(null); }}>
                            Cancel
                        </button>
                        <button className="btn" onClick={handleResetPassword} disabled={!!resettingId}>
                            {resettingId ? <><i className="fas fa-spinner fa-spin"></i> Resetting…</> : 'Reset Password'}
                        </button>
                    </>}
                >
                    <p className="admin-modal-text">
                        Their current password stops working immediately, and they'll be forced to set their own
                        password at next login. Nothing is emailed — you share this with them directly.
                    </p>

                    <div className="form-group">
                        <label htmlFor="reset-password">Temporary Password</label>
                        <div className="admin-generate-row">
                            <input
                                id="reset-password"
                                type="text"
                                value={confirmReset.password}
                                onChange={(e) => {
                                    const password = e.target.value;
                                    setConfirmReset((prev) => ({ ...prev, password }));
                                    setResetError(null);
                                }}
                            />
                            <button
                                type="button" className="btn btn-secondary" title="Generate a new one"
                                onClick={() => {
                                    setConfirmReset((prev) => ({ ...prev, password: generateTempPassword() }));
                                    setResetError(null);
                                }}
                            >
                                <i className="fas fa-rotate"></i> Regenerate
                            </button>
                        </div>
                        {resetError
                            ? <span className="text-danger pt-2">{resetError}</span>
                            : <span className="admin-field-note">{PASSWORD_HINT}</span>}
                    </div>
                </Popup>
            )}

            {/* ── Reset result / temporary password ─────────────────────────── */}
            {resetResult && (
                <Popup
                    title={`Password reset for ${resetResult.admin.name}`}
                    icon="fas fa-check-circle"
                    iconColor="#10b981"
                    maxWidth={500}
                    onClose={() => { setResetResult(null); fetchData(); }}
                    footer={
                        <button className="btn" onClick={() => { setResetResult(null); fetchData(); }}>Done</button>
                    }
                >
                    <p className="admin-modal-text">
                        Copy this temporary password and share it with them directly. Once you close this dialog it
                        cannot be shown again.
                    </p>
                    <div className="admin-temp-password">
                        <code>{resetResult.tempPassword}</code>
                        <button className="btn btn-secondary" onClick={() => copyToClipboard(resetResult.tempPassword)}>
                            <i className="fas fa-copy"></i> Copy
                        </button>
                    </div>

                    <p className="admin-modal-hint">
                        <i className="fas fa-info-circle"></i>
                        They'll be sent straight to the Change Password screen at next login and cannot reach any
                        section until they set their own password.
                    </p>
                </Popup>
            )}

            {/* ── Confirm delete ────────────────────────────────────────────── */}
            {confirmDelete && (
                <Popup
                    title={`Remove ${confirmDelete.name}?`}
                    icon="fas fa-exclamation-triangle"
                    iconColor="#ef4444"
                    maxWidth={460}
                    onClose={() => setConfirmDelete(null)}
                    footer={<>
                        <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                        <button className="btn btn-danger-solid" onClick={handleDelete} disabled={deleting}>
                            {deleting ? <><i className="fas fa-spinner fa-spin"></i> Removing…</> : 'Remove'}
                        </button>
                    </>}
                >
                    <p className="admin-modal-text">
                        This permanently removes their admin account and signs them out. This cannot be undone.
                    </p>
                </Popup>
            )}

            <style>{`
                .admin-modal-text {
                    color: var(--body-text-color); font-size: 14px;
                    line-height: 1.6; margin: 0 0 14px;
                }
                .admin-modal-hint {
                    display: flex; align-items: flex-start; gap: 8px;
                    font-size: 12.5px; color: #8a8a9a; line-height: 1.55; margin: 0;
                }
                .admin-field-note {
                    display: flex; align-items: flex-start; gap: 6px;
                    font-size: 12px; color: #8a8a9a; margin-top: 6px; line-height: 1.5;
                }
                .admin-field-note.locked { color: #f59e0b; }
                .admin-generate-row { display: flex; gap: 8px; align-items: stretch; }
                .admin-generate-row input {
                    flex: 1; min-width: 0;
                    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                    letter-spacing: 0.5px;
                }
                .admin-generate-row .btn { white-space: nowrap; flex-shrink: 0; }
                @media (max-width: 480px) {
                    .admin-generate-row { flex-direction: column; }
                }
                .admin-temp-password {
                    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
                    padding: 12px 14px; margin-bottom: 16px; border-radius: 8px;
                    background: rgba(138,138,154,0.12);
                    border: 1px dashed rgba(138,138,154,0.40);
                }
                .admin-temp-password code {
                    flex: 1; min-width: 160px; font-size: 16px;
                    letter-spacing: 1px; word-break: break-all;
                    color: var(--white, #fff);
                }
                .btn-danger-solid {
                    background: #ef4444 !important;
                    border-color: #ef4444 !important;
                    color: #fff !important;
                }
            `}</style>
        </section>
    );
};

export default AdminUsers;
